import type {
  ProdStateAnomaly,
  PromotionBox,
  PromotionBoxLine,
  VersionedKind,
} from '@matter_extractor/shared';

/**
 * What the screen « État de la prod » says of each line (vitrine.md §9.3, lot F of issue #192;
 * maquette `vitrine/atelier.html`, section 2) - pure, so the classification is tested without a
 * server.
 *
 * A candidate is an entity whose « latest dev » is ahead of « latest prod » (the atelier lists them,
 * `GET /api/atelier/prod-state`). The prod JUDGES each one at blank when the screen opens, by the
 * same check the box of the cascade asks for (`POST /api/atelier/promotion/check`), and the box that
 * comes back decides the group:
 *
 * - `ok` -> « Prêt à passer », the green badge, and what the cascade takes along;
 * - `refused` -> « Attend un tag »: a red « refus » with the reason of the prod when it is the root
 *   that is refused, a yellow « en attente » when it is another version of the cascade (the cascade
 *   goes whole or not at all, so the root waits for it);
 * - no verdict (the prod did not answer, the answer is still on its way) -> the line stays in
 *   « Prêt à passer », with a grey badge and an inert button: it was not refused, and it cannot go.
 */

/** The eyebrow of a line: the four libraries, and « Réglages » for the four singletons. */
export const KIND_EYEBROW: Record<VersionedKind, string> = {
  criterion: 'Critère',
  persona: 'Persona',
  protocol: 'Protocole',
  experience: 'Expérience',
  'noyau-texts': 'Réglages',
  models: 'Réglages',
  rules: 'Réglages',
  method: 'Réglages',
};

export type VerdictBadge = 'ok' | 'refus' | 'attente' | 'pas-jugee' | 'jugement';

export type Verdict = {
  group: 'ready' | 'waiting';
  badge: VerdictBadge;
  /** The badge, as written on it. */
  badgeText: string;
  /** The sentence after the badge. */
  text: string;
  /** Whether « Passer en production » is live: only a line the prod accepted, while the prod answers. */
  passable: boolean;
};

const BADGE_TEXT: Record<VerdictBadge, string> = {
  ok: 'OK',
  refus: 'refus',
  attente: 'en attente',
  'pas-jugee': 'pas jugée',
  jugement: 'jugement…',
};

const verdict = (
  group: Verdict['group'],
  badge: VerdictBadge,
  text: string,
  passable = false,
): Verdict => ({ group, badge, badgeText: BADGE_TEXT[badge], text, passable });

/** « Maya v2 » - a version of the cascade, named. */
const named = (line: Pick<PromotionBoxLine, 'entityName' | 'versionNumber'>): string =>
  `${line.entityName} v${line.versionNumber}`;

/**
 * What the cascade takes along, for the sentence of an accepted line: « emmène : Maya v2, 3 fichiers »
 * or « emmène : rien d'autre », then what the prod holds already - « ; Scénario de succès v17 y est
 * déjà ». The root is the gesture's own version: it is not « d'autre ».
 */
export function carriesOf(box: Pick<PromotionBox, 'lines' | 'assets'>): string {
  const others = box.lines.filter((line) => !line.root);
  const sends = others.filter((line) => line.status === 'sends').map(named);
  const files = box.assets.filter((asset) => asset.status === 'sends').length;
  const parts = [
    ...sends,
    ...(files === 0 ? [] : [`${files} ${files === 1 ? 'fichier' : 'fichiers'}`]),
  ];
  const already = others.filter((line) => line.status === 'already').map(named);
  const carries = `emmène : ${parts.length === 0 ? 'rien d’autre' : parts.join(', ')}`;
  return already.length === 0
    ? carries
    : `${carries} ; ${already.join(', ')} ${already.length === 1 ? 'y est' : 'y sont'} déjà`;
}

/**
 * The verdict of a line from the box the prod last rendered for it, or `null` when it never did.
 * `pending` is a judgment still on its way.
 */
export function verdictOf(box: PromotionBox | null, pending: boolean): Verdict {
  if (box === null || box.state === 'unreachable' || box.state === 'local') {
    return pending
      ? verdict('ready', 'jugement', 'la prod juge cette ligne à blanc')
      : verdict(
          'ready',
          'pas-jugee',
          'la prod ne l’a pas jugée : rien ne peut passer pour l’instant',
        );
  }
  if (box.state === 'ok') {
    const by = box.prodCode === null ? 'relue par la prod' : `relue par la prod (${box.prodCode})`;
    return verdict('ready', 'ok', `${by} · ${carriesOf(box)}`, true);
  }

  // Refused. The gesture's own refusal, then the root, then the rest of the cascade.
  if (box.gestureRefusal !== undefined) {
    return verdict('waiting', 'refus', box.gestureRefusal.message);
  }
  const root = box.lines.find((line) => line.root && line.status === 'refused');
  if (root !== undefined) {
    return verdict('waiting', 'refus', root.reason?.message ?? 'La prod refuse cette version.');
  }
  const others = box.lines.filter((line) => line.status === 'refused');
  if (others.length > 0) {
    return verdict(
      'waiting',
      'attente',
      `emmène ${others.map(named).join(', ')}, qui attend${others.length > 1 ? 'ent' : ''} un tag : la cascade passe entière ou pas du tout`,
    );
  }
  const file = box.assets.find((asset) => asset.status === 'refused');
  return verdict('waiting', 'refus', file?.reason?.message ?? 'La prod refuse ce passage.');
}

/** Where the name of a line leads: the editor of the entity. */
export type EditorTarget =
  | { to: '/admin/criteres/$criterionId'; params: { criterionId: string } }
  | { to: '/admin/personas/$personaId'; params: { personaId: string } }
  | { to: '/admin/protocoles/$protocolId'; params: { protocolId: string } }
  | { to: '/admin/experiences/$experienceId'; params: { experienceId: string } }
  | { to: '/admin/reglages/noyaux' }
  | { to: '/admin/reglages/modeles' }
  | { to: '/admin/reglages/regles' }
  | { to: '/admin/reglages/methode' };

export function editorTarget(kind: VersionedKind, entityId: string): EditorTarget {
  switch (kind) {
    case 'criterion':
      return { to: '/admin/criteres/$criterionId', params: { criterionId: entityId } };
    case 'persona':
      return { to: '/admin/personas/$personaId', params: { personaId: entityId } };
    case 'protocol':
      return { to: '/admin/protocoles/$protocolId', params: { protocolId: entityId } };
    case 'experience':
      return { to: '/admin/experiences/$experienceId', params: { experienceId: entityId } };
    case 'noyau-texts':
      return { to: '/admin/reglages/noyaux' };
    case 'models':
      return { to: '/admin/reglages/modeles' };
    case 'rules':
      return { to: '/admin/reglages/regles' };
    case 'method':
      return { to: '/admin/reglages/methode' };
  }
}

/** « en prod : v5 » or « jamais en prod ». */
export function productionNote(production: { number: number } | null): string {
  return production === null ? 'jamais en prod' : `en prod : v${production.number}`;
}

/** The words of the two reasons of an anomaly (§8.2). */
export function anomalyReason(reason: ProdStateAnomaly['reason']): string {
  return reason === 'content'
    ? 'même identifiant, autre contenu'
    : 'numéro pris par une autre version';
}

/** « 16 oct. à 14:00 » - the day and the hour a stamp falls on, local time. */
export function dayAndHour(stamp: string): string {
  const at = new Date(stamp);
  if (Number.isNaN(at.getTime())) return '';
  const day = at.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  const two = (value: number) => String(value).padStart(2, '0');
  return `${day} à ${two(at.getHours())}:${two(at.getMinutes())}`;
}

/** The two contents of an anomaly, as indented JSON for the side-by-side view. */
export function prettyContent(content: Record<string, unknown> | null): string {
  return content === null
    ? 'Le dev ne connaît pas cette version.'
    : JSON.stringify(content, null, 2);
}

/** The red note when the prod does not answer: since when, and what it means for the lines. */
export function unreachableNote(since: string | null): string {
  const when = since === null || since === '' ? '' : ` depuis ${since}`;
  return `La prod ne répond pas${when} : elle n’a pas pu juger les lignes à l’ouverture de cet écran, aucun passage n’est possible, et rien n’a bougé. Les verdicts ci-dessous sont les derniers qu’elle a rendus.`;
}

export const NO_PROD_SENTENCE =
  'Aucune prod reliée : PROD_ORIGIN n’est pas posé sur ce serveur, il n’y a pas d’état de la prod à montrer.';

export const SCREEN_TITLE = 'État de la prod';

export const SCREEN_LEAD =
  'Ce que le dev a d’avance sur la prod, objet par objet. Chaque ligne est jugée à blanc par la prod, avec son propre code, à l’ouverture de cet écran, entité par entité : rien ne passe tant qu’on n’a pas cliqué.';

export const EMPTY_ANOMALIES =
  'Aucune anomalie : chaque version reçue de la prod porte le contenu que le dev lui connaît. Une anomalie, c’est une version au même identifiant et à un autre contenu ; elle est journalisée et s’affiche ici, avec ses deux contenus.';
